import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface EliminationSeed {
  seed: number;
  player1Name: string;
  player2Name: string | null;
  fromGroup: string;
  qualificationType: string;
  points: number;
  setsWon: number;
  setsLost: number;
  gamesWon: number;
  gamesLost: number;
  registrationId: string | null;
  isUser: boolean;
}

export function useEliminationSeeding(
  eventId: string,
  category: string | null,
  subcategory: string | null
) {
  const [seeding, setSeeding] = useState<EliminationSeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSeeding() {
      if (!eventId || !category || !subcategory) {
        setLoading(false);
        return;
      }

      try {
        // Get current user to mark their team
        const { data: { user } } = await supabase.auth.getUser();

        // Fetch seeding data and event format in parallel
        const [seedingResult, eventResult] = await Promise.all([
          supabase
            .from('tournament_elimination_seeding')
            .select('*')
            .eq('tournament_id', eventId)
            .eq('category', category)
            .eq('subcategory', subcategory)
            .order('seed', { ascending: true }),
          supabase
            .from('events')
            .select('tournament_format')
            .eq('id', eventId)
            .single()
        ]);

        if (seedingResult.error) throw seedingResult.error;
        const data = seedingResult.data;
        const tournamentFormat = eventResult.data?.tournament_format;

        // Get user's registration to identify their team.
        // Se acota a esta categoría/subcategoría: un usuario inscrito en varias tiene
        // varias registrations y maybeSingle() fallaba (más de una fila), dejando sin
        // resaltar su pareja. Además la pareja correcta depende de la categoría.
        let userRegId: string | null = null;
        if (user?.id) {
          const { data: userRegs } = await supabase
            .from('registrations')
            .select('id')
            .eq('event_id', eventId)
            .eq('category', category)
            .eq('subcategory', subcategory)
            .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
            .eq('status', 'confirmed')
            .limit(1);

          userRegId = userRegs?.[0]?.id || null;
        }

        const seedingData: EliminationSeed[] = (data || []).map(row => ({
          seed: row.seed,
          player1Name: row.player1_name,
          player2Name: row.player2_name,
          fromGroup: row.from_group,
          qualificationType: row.qualification_type,
          points: row.points || 0,
          setsWon: row.sets_won || 0,
          setsLost: row.sets_lost || 0,
          gamesWon: row.games_won || 0,
          gamesLost: row.games_lost || 0,
          registrationId: row.registration_id,
          isUser: row.registration_id === userRegId
        }));

        setSeeding(seedingData);
        setError(null);
      } catch (err) {
        console.error('[useEliminationSeeding] Error:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar seeding');
      } finally {
        setLoading(false);
      }
    }

    fetchSeeding();

    // Real-time subscription
    const channel = supabase
      .channel(`elimination-seeding-${eventId}-${category}-${subcategory}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tournament_elimination_seeding',
        filter: `tournament_id=eq.${eventId}`
      }, () => {
        fetchSeeding();
      })
      .subscribe();

    const pollInterval = setInterval(() => {
      fetchSeeding();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [eventId, category, subcategory]);

  return { seeding, loading, error };
}
